/**
 * Newsletter subscription (the "exclusive offers" box on the home page).
 *
 * Uses a bare axios client for the same reason `authApi` does: `service/axios`
 * carries an interceptor that treats 401 as "session expired" and bounces to the
 * home page. This call has no session and is made by visitors who are not signed
 * in, so that behaviour would be actively wrong here.
 */

import axios from 'axios';

import service from './constant';

const subscriberClient = axios.create({
    baseURL: service.API_URL + '/api/v1/client',
    headers: { 'Content-Type': 'application/json' }
});

export interface SubscribeResult {
    ok: boolean;
    message: string;
}

/**
 * Never throws. The caller renders whatever comes back as an inline message, and
 * an unhandled rejection in a submit handler would leave the form stuck in its
 * busy state with nothing on screen explaining why.
 *
 * The server answers the same 200 for a new address, one already on the list and
 * one that had unsubscribed - it will not confirm whether an address is known,
 * which would make the box an oracle for anyone holding a list of emails. So
 * there is nothing here that distinguishes those cases either.
 */
export const subscribeToNewsletter = async (email: string): Promise<SubscribeResult> => {
    try {
        const { data } = await subscriberClient.post('/subscribe', { email });
        return {
            ok: true,
            message: data?.message || "You're on the list. Check your inbox for a welcome from us."
        };
    } catch (error: any) {
        const httpStatus = error?.response?.status;
        const serverMessage = error?.response?.data?.message;

        if (httpStatus === 429) {
            return { ok: false, message: serverMessage || 'Too many requests. Please wait a moment and try again.' };
        }
        if (httpStatus === 400) {
            return { ok: false, message: serverMessage || 'Please enter a valid email address.' };
        }
        return { ok: false, message: serverMessage || 'Something went wrong. Please try again.' };
    }
};
